import { useEffect, useMemo, useRef, useState } from 'react';
import { track } from '@/lib/telemetry';
import { Card } from '@/components/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { EChart, type EChartsOption } from '@/components/charts/EChart';
import { buildTooltip } from '@/components/charts/EChartTooltip';
import { formatBRLCompact } from '@/lib/charts/formatters';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import type { RecurringMonthlyPoint } from '@/lib/api';
import { RECURRING_TAXONOMY, useRecurringTaxonomyColors } from './taxonomy';

interface Props {
  year: RecurringMonthlyPoint[];
  currentMonthIndex: number;
}

const SERIES_ORDER = ['fixas', 'parcelamentos', 'assinaturas'] as const;
/** Below this width the 12 bars get cramped, so we page by semester instead. */
const SHOW_ALL_WIDTH = 560;

function pointTotal(p: RecurringMonthlyPoint): number {
  return p.fixas + p.parcelamentos + p.assinaturas;
}

/** "Compromissos por mês": stacked bars, current month highlighted, shared year scale. */
export function CommitmentsYearChart({ year, currentMonthIndex }: Props) {
  const formatCurrency = useFormatCurrency();
  const taxonomyColors = useRecurringTaxonomyColors();
  const chartRef = useRef<HTMLDivElement | null>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const showAll = chartWidth === 0 || chartWidth >= SHOW_ALL_WIDTH;

  // Default to the semester containing the current month.
  const [half, setHalf] = useState(currentMonthIndex < 6 ? '0' : '1');

  useEffect(() => {
    const element = chartRef.current;
    if (!element) return undefined;
    const update = () => {
      const next = Math.round(element.getBoundingClientRect().width);
      setChartWidth((prev) => (prev === next ? prev : next));
    };
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver((entries) => {
      const next = Math.round(entries[0]?.contentRect.width ?? 0);
      setChartWidth((prev) => (prev === next ? prev : next));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Shared scale across the whole year so the two semesters stay comparable.
  const yearMax = useMemo(() => year.reduce((max, p) => Math.max(max, pointTotal(p)), 0), [year]);
  const peak = useMemo(() => {
    let best = year[0];
    for (const p of year) if (pointTotal(p) > pointTotal(best)) best = p;
    return best ? { label: best.label, value: pointTotal(best) } : null;
  }, [year]);

  const offset = showAll ? 0 : Number(half) * 6;
  const points = showAll ? year : year.slice(offset, offset + 6);

  const option = useMemo<EChartsOption>(() => {
    const localCurrent = currentMonthIndex - offset;
    const dim = (i: number) => (i === localCurrent ? 1 : 0.32);
    const labels = points.map((p) => p.label);

    const series = SERIES_ORDER.map((key, idx) => ({
      name: RECURRING_TAXONOMY[key].label,
      type: 'bar' as const,
      stack: 'commitments',
      barMaxWidth: showAll ? 36 : 44,
      data: points.map((p, i) => ({
        value: p[key],
        itemStyle: {
          color: taxonomyColors[key],
          opacity: dim(i),
          borderRadius:
            idx === 0 ? [0, 0, 3, 3] : idx === SERIES_ORDER.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0],
        },
      })),
    }));

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        confine: true,
        formatter: buildTooltip({ showTotal: true, hidePercent: true }),
      },
      grid: { left: 4, right: 8, top: 8, bottom: 8, containLabel: true },
      xAxis: {
        type: 'category',
        data: labels,
        axisTick: { show: false },
        axisLabel: { fontSize: 11, color: '#6B736E' },
      },
      yAxis: {
        type: 'value',
        max: yearMax > 0 ? yearMax : undefined,
        axisLabel: { formatter: (v: number) => formatBRLCompact(v) },
        splitLine: { lineStyle: { color: '#F0F1F0' } },
      },
      series,
    };
  }, [points, offset, currentMonthIndex, showAll, yearMax, taxonomyColors]);

  return (
    <Card variant="flat" className="min-w-0 rounded-[var(--radius-3xl)] border-border bg-surface p-4 shadow-[var(--shadow-1)] sm:p-6">
      <div className="mb-4 flex min-w-0 items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-text-primary sm:text-sm">Compromissos por mês</h2>
          <p className="mt-0.5 text-sm text-text-tertiary sm:text-xs">Projeção anual das recorrências ativas</p>
        </div>
        {!showAll && (
          <ToggleGroup
            type="single"
            value={half}
            onValueChange={(value) => {
              if (!value) return;
              track('ui.tab_viewed', {
                surface: 'recurring_chart',
                tab: value === '0' ? 'jan_jun' : 'jul_dez',
                trigger: 'switch',
              });
              setHalf(value);
            }}
            className="shrink-0 gap-0.5 rounded-lg border border-border bg-surface-sunken p-0.5"
          >
            {['Jan–Jun', 'Jul–Dez'].map((label, i) => (
              <ToggleGroupItem
                key={label}
                value={String(i)}
                className="rounded-md px-2.5 py-1 text-2xs data-[state=on]:shadow-sm"
              >
                {label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        )}
      </div>
      <div ref={chartRef} className="h-64 w-full min-w-0">
        <EChart option={option} notMerge />
      </div>
      {peak && peak.value > 0 && (
        <p className="mt-1.5 px-1 text-2xs text-text-tertiary">
          Escala fixa no ano · maior mês <span className="font-medium text-text-secondary">{peak.label}</span>,{' '}
          <span className="font-mono tabular-nums">{formatCurrency(peak.value, { compact: true })}</span>
        </p>
      )}
    </Card>
  );
}
